<template>
  <div>
    <!-- Page Header -->
    <CCard class="mb-4">
      <CCardBody class="p-4">
        <h1 class="h4 mb-1 font-weight-bold">
          {{ obj.id ? `Customer PO ${obj.orderNo || ""}` : "New customer PO" }}
        </h1>
        <p class="text-muted mb-0">
          Keep a record of the purchase orders your customers send you
        </p>
      </CCardBody>
    </CCard>

    <CRow>
      <CCol lg="10" xl="8">
        <CCard class="mb-4">
          <CCardHeader class="bg-transparent border-0 p-4">
            <h2 class="h6 font-weight-bold mb-0">Order details</h2>
          </CCardHeader>
          <CCardBody class="p-4">
            <div ref="customerField" class="form-group" tabindex="-1">
              <label for="sales-order-customer">Customer *</label>
              <v-select
                input-id="sales-order-customer"
                style="width: 100%"
                :class="{ 'is-invalid': errors.customer }"
                v-model="selectedCustomer"
                :label="'name'"
                :options="customerItemsWithAddNew"
                placeholder="Select customer"
                @input="handleCustomerSelect"
              />
              <div v-if="errors.customer" class="invalid-feedback d-block">
                {{ errors.customer }}
              </div>
            </div>

            <CRow>
              <CCol md="6">
                <CInput
                  ref="orderNoField"
                  label="Order number *"
                  v-model="obj.orderNo"
                  placeholder="The number on your customer's PO"
                  :is-valid="errors.orderNo ? false : null"
                  :invalid-feedback="errors.orderNo"
                  @input="revalidate"
                />
              </CCol>
              <CCol md="6">
                <CInput
                  ref="dateField"
                  label="Date *"
                  type="date"
                  :value="computeDate"
                  :is-valid="errors.date ? false : null"
                  :invalid-feedback="errors.date"
                  @change="setDate"
                />
              </CCol>
            </CRow>

            <CInput
              ref="amountField"
              label="Amount *"
              v-model="obj.totalAmount"
              inputmode="decimal"
              prepend="RM"
              placeholder="0.00"
              :is-valid="errors.totalAmount ? false : null"
              :invalid-feedback="errors.totalAmount"
              @input="revalidate"
            />

            <div class="form-group">
              <label>Document</label>
              <div v-if="obj.documentId" class="mb-3">
                <iframe
                  v-if="isPdf(obj.document)"
                  :src="getDocumentUrl()"
                  title="Purchase order document"
                  width="100%"
                  height="600"
                  class="border rounded"
                ></iframe>
                <CImg
                  v-else
                  :src="getDocumentUrl()"
                  :alt="`Purchase order ${obj.orderNo || ''}`"
                  fluid
                />
                <a
                  :href="getDocumentUrl()"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="d-inline-block mt-2"
                >
                  Open {{ getDocumentName() }} in a new tab
                </a>
              </div>
              <WidgetsUploadDocument @uploaded="uploaded" />
            </div>
          </CCardBody>
          <CCardFooter class="bg-transparent d-flex justify-content-between p-4">
            <CButton color="light" :disabled="saving" @click="cancel">Cancel</CButton>
            <CButton color="primary" :disabled="saving" @click="submit">
              <CSpinner v-if="saving" size="sm" class="mr-1" />
              {{ obj.id ? "Save" : "Add customer PO" }}
            </CButton>
          </CCardFooter>
        </CCard>
      </CCol>
    </CRow>

    <CustomerFormModal
      :show="addCustomerFormPopup"
      :customer="itemAddNewCustomer"
      @close="addCustomerFormPopup = false"
      @save="onCustomerPopupConfirmation"
    />
  </div>
</template>

<script>
import SalesOrderApi from "@/lib/salesOrderApi";
import WidgetsUploadDocument from "../widgets/WidgetsUploadDocument.vue";
import CustomerFormModal from "../components/CustomerFormModal";
import CustomerApi from "@/lib/customerApi";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import { mytToday, toApiDate, asDateString } from "@/utils/datetime";

export default {
  name: "SalesOrder",
  components: {
    vSelect,
    WidgetsUploadDocument,
    CustomerFormModal,
  },
  data: () => {
    return {
      selectedCustomer: null,
      customerItems: [],
      customerApi: new CustomerApi(),
      itemAddNewCustomer: {},
      addCustomerFormPopup: false,

      uploadedFiles: [],
      orderDate: "",
      obj: {
        orderNo: "",
      },
      submitted: false,
      saving: false,
      errors: {},
      api: new SalesOrderApi(),
    };
  },
  mounted() {
    this.$store.commit("setPageNavItems", ["Sales", "Customer P.O."]);
    this.orderDate = mytToday();
    this.refreshCustomer();
    this.resetObj();
  },
  computed: {
    customerItemsWithAddNew() {
      return [
        ...this.customerItems,
        { id: "add_new", name: "+ Add new customer" },
      ];
    },
    computeDate() {
      return asDateString(this.orderDate);
    },
  },
  methods: {
    refreshCustomer() {
      this.customerApi
        .getListByCurrentBusiness()
        .then((response) => {
          this.customerItems = response.result;
        })
        .catch(() => {});
    },

    onCustomerPopupConfirmation(customer) {
      if (customer) {
        this.customerApi
          .create(customer)
          .then((response) => {
            this.refreshCustomer();
            this.selectedCustomer = response.result;
            this.$toast.success("Customer added");
            this.revalidate();
          })
          .catch(({ data } = {}) => {
            this.$toast.error(`Couldn't add the customer. ${helper.getErrorMessage(data)}`);
          });
      }
      this.addCustomerFormPopup = false;
      this.itemAddNewCustomer = {};
    },
    handleCustomerSelect(selected) {
      if (selected && selected.id === "add_new") {
        this.addNewCustomer();
        this.selectedCustomer = null;
      }
      this.revalidate();
    },
    addNewCustomer() {
      this.itemAddNewCustomer = {};
      this.addCustomerFormPopup = true;
    },
    isPdf(document) {
      return !!(document && document.contentType === "application/pdf");
    },
    cancel() {
      this.$router.push({ path: "/tenants/SalesOrderList" });
    },
    getDocumentUrl() {
      return apiUrl + "documents/file/" + this.obj.documentId;
    },
    getDocumentName() {
      if (this.obj.document == null || this.obj.document.fileName == null) return "the document";
      return this.obj.document.fileName;
    },
    uploaded(data) {
      this.uploadedFiles = (data && data.uploadedFiles) || [];
      if (!this.uploadedFiles.length) return;
      const file = this.uploadedFiles[0];
      this.$set(this.obj, "documentId", file.id);
      this.$set(this.obj, "document", { ...(this.obj.document || {}), fileName: file.fileName, contentType: file.contentType });
    },
    setDate(e) {
      this.orderDate = e;
      this.revalidate();
    },

    validate() {
      const errors = {};
      if (!this.selectedCustomer || this.selectedCustomer.id === "add_new") {
        errors.customer = "Choose the customer who sent this PO.";
      }
      if (!this.obj.orderNo || !String(this.obj.orderNo).trim()) {
        errors.orderNo = "Enter the order number from the customer's PO.";
      }
      if (!asDateString(this.orderDate)) {
        errors.date = "Enter the date on the PO.";
      }
      const amount = this.obj.totalAmount;
      if (amount === undefined || amount === null || String(amount).trim() === "" || isNaN(Number(amount))) {
        errors.totalAmount = "Enter the PO amount, for example 1250.00.";
      } else if (Number(amount) < 0) {
        errors.totalAmount = "The amount can't be negative.";
      }
      this.errors = errors;
      return Object.keys(errors).length === 0;
    },
    revalidate() {
      if (this.submitted) this.validate();
    },
    focusFirstInvalid() {
      this.$nextTick(() => {
        const order = [
          ["customer", "customerField"],
          ["orderNo", "orderNoField"],
          ["date", "dateField"],
          ["totalAmount", "amountField"],
        ];
        const hit = order.find(([key]) => this.errors[key]);
        if (!hit) return;
        const ref = this.$refs[hit[1]];
        const el = ref && (ref.$el || ref);
        if (!el) return;
        if (el.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "center" });
        const input = el.querySelector ? el.querySelector("input") : null;
        const target = input || el;
        if (target.focus) target.focus();
      });
    },

    resetObj() {
      var self = this;
      if (self.$route.params.id) {
        self.api
          .get(self.$route.params.id)
          .then((response) => {
            self.obj = response.result;
            self.orderDate = asDateString(self.obj.date);
            self.selectedCustomer = self.obj.customer;
          })
          .catch(({ data } = {}) => {
            self.$toast.error(`Couldn't load the customer PO. ${helper.getErrorMessage(data)}`);
          });
      } else {
        self.obj = { orderNo: "" };
      }
    },
    onSubmit() {
      var self = this;
      if (self.saving) return;
      if (!self.validate()) {
        self.focusFirstInvalid();
        return;
      }
      self.saving = true;
      // Date-only field goes to the API as "yyyy-MM-dd" (never a Date: JSON turns it into UTC, 8 hours back)
      self.obj.date = toApiDate(asDateString(self.orderDate)) || null;
      if (self.selectedCustomer) {
        self.obj.customerId = self.selectedCustomer.id;
      }
      if (!self.obj.id) {
        this.api
          .create(self.obj)
          .then((response) => {
            self.obj = response.result;
            self.$toast.success("Customer PO added");
            self.$router.push({ path: `/tenants/SalesOrder/${self.obj.id}` });
          })
          .catch(({ data } = {}) => {
            self.$toast.error(`Couldn't add the customer PO. ${helper.getErrorMessage(data)}`);
          })
          .finally(() => {
            self.saving = false;
          });
      } else {
        this.api
          .update(self.obj)
          .then(() => {
            self.$toast.success("Customer PO saved");
          })
          .catch(({ data } = {}) => {
            self.$toast.error(`Couldn't save the customer PO. ${helper.getErrorMessage(data)}`);
          })
          .finally(() => {
            self.saving = false;
          });
      }
    },
    submit() {
      this.submitted = true;
      this.onSubmit();
    },
  },
};
</script>

<style scoped>
.is-invalid ::v-deep .vs__dropdown-toggle {
  border-color: var(--danger);
}
</style>
